import { useMemo, useState } from 'react';

import useAdvanceTable from 'hooks/useAdvanceTable';
import { useTableController } from 'hooks/useTableController';
import { folderColumn } from 'helpers/folderPaths';
import TableViewLayout from 'layouts/TableViewLayout';

import { fetchSchedulingWindows } from '../../../../redux/slices/scheduling_windowsSlice';
import type { SchedulingWindowRow } from 'api/scheduling_windows/endpoints/getSchedulingWindows';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { getActions, ActionButton, ActionDropdown, type ActionEntity } from 'actions';

// `zone_name` is nullable on the wire (a window whose zone was deleted), and the
// action entity needs a string; such a window cannot be acted on by zone anyway.
const toActionEntity = (row: SchedulingWindowRow): ActionEntity => ({
  type: 'scheduling_window',
  id: row.scheduling_window_id,
  zoneName: row.zone_name ?? '',
  name: row.name,
});

const ListSchedulingWindows = () => {
  const dispatch = useAppDispatch();

  const userData = useAppSelector((state) => state.auth.userData);
  const { nodes, config: headerConfig, filtersConfig, pagination, error, loading } = useAppSelector((state) => state.schedulingWindow);

  const isLoading = loading?.fetchSchedulingWindows ?? false;

  const pageName = 'scheduling-windows';
  const [showSidebar, setShowSidebar] = useState(false);

  const refreshList = () => {
    dispatch(fetchSchedulingWindows({ page, limit }));
  };

  const {
    filters,
    setFilterAndSync,
    resetFilters,
    updateSort,
    updatePage,
    updateLimit,
    limit,
    page,
    updateUserSettings,
    getFormattedData,
    generateColumns,
    refresh,
    isSilentRefresh,
  } = useTableController({
    userData,
    pageName,
    filtersConfig,
    headerConfig,
    fetchData: fetchSchedulingWindows,
    columnFormats: { created_at: 'datetime', updated_at: 'datetime' },
  });

  const actionsColumn = {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const actions = getActions({ entities: [toActionEntity(row.original as SchedulingWindowRow)], refresh: refreshList });
      return <ActionDropdown actions={actions} items={['edit', 'duplicate', 'previewOpenPeriods', 'addToTransportSchedulingWindow', 'delete']} />;
    },
  };

  const baseColumns = generateColumns((key, order) => {
    updateSort(key, order);
    updateUserSettings(key, order);
  });

  const columns = [...baseColumns, folderColumn, actionsColumn];

  const formattedData = useMemo(() => getFormattedData(nodes), [nodes]);

  const table = useAdvanceTable({
    data: formattedData,
    columns,
    pageSize: limit,
    pagination: true,
    sortable: true,
    selection: true,
    getRowId: (row) => String(row.scheduling_window_id),
  });

  // One resolution against the current multi-selection: the create static is
  // always present; addToTransport appears (disabled) until a row is selected.
  const selectionEntities = table.getSelectedRowModel().rows.map((r: any) => toActionEntity(r.original as SchedulingWindowRow));
  const toolbarActions = getActions({ entities: selectionEntities, refresh: refreshList });

  return (
    <TableViewLayout
      filtersConfig={filtersConfig}
      filters={filters}
      pageName={pageName}
      columns={headerConfig.map((c) => ({
        key: c.header_key,
        label: c.label,
      }))}
      pageSize={limit}
      error={error}
      onFilterChange={setFilterAndSync}
      onResetFilters={resetFilters}
      onPageSizeChange={updateLimit}
      currentPage={pagination.currentPage}
      totalPages={pagination.totalPages}
      totalItems={pagination.totalItems}
      onPageChange={updatePage}
      table={table}
      showSidebar={showSidebar}
      setShowSidebar={setShowSidebar}
      onRefresh={refresh}
      isLoading={isLoading}
      isSilentRefresh={isSilentRefresh}
      toolbarButtons={
        <>
          <ActionButton actions={toolbarActions} action="createSchedulingWindow" />
          <ActionButton
            actions={toolbarActions}
            action="addToTransportSchedulingWindow"
            className="ms-2"
            variant="secondary"
            label="Add selected to transport"
          />
        </>
      }
    />
  );
};

export default ListSchedulingWindows;
